import { supabase } from '../lib/supabase'
import { loadStripe } from '@stripe/stripe-js'

// Initialize Stripe with better error handling
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RhIKlGLiG8YFB1K5QL3hOuPB6tvHVL3NHhtUYQQtiSvDpyoe8JkAbt1WkFkTGKI7riDiNKK0psn8X0eDcoxum3I00NcE4PDXl'

let stripePromise: Promise<any> | null = null

// Only initialize Stripe if we have a valid key
if (STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY !== '') {
  stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)
} else {
  console.warn('Stripe publishable key not configured - gift payments will be disabled')
  stripePromise = Promise.resolve(null)
}

export interface GiftOption {
  id: string
  type: 'subscription' | 'credits'
  title: string
  description: string
  price: number
  valueAmount: number
  duration?: string
  features: string[]
}

export interface GiftPurchaseData {
  giftType: 'subscription' | 'credits'
  giftId: string
  title: string
  description: string
  price: number
  valueAmount: number
  purchaserName: string
  recipientEmail: string
  giftMessage?: string
}

export interface GiftRecord {
  id: string
  gift_type: 'subscription' | 'credits'
  title: string
  description: string
  price: number
  value_amount: number
  purchaser_name: string
  recipient_email: string
  gift_message?: string
  gift_code: string
  status: 'pending' | 'sent' | 'redeemed' | 'expired' | 'cancelled'
  payment_status: 'pending' | 'succeeded' | 'failed' | 'cancelled'
  created_at: string
  sent_at?: string
  redeemed_at?: string
  expires_at: string
}

export class GiftService {
  /**
   * Create a gift payment intent and process the purchase
   */
  static async purchaseGift(giftData: GiftPurchaseData): Promise<{
    clientSecret: string
    giftId: string
    giftCode: string
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('User must be authenticated to purchase gifts')
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-gift-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(giftData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to process gift payment')
      }

      return await response.json()
    } catch (error) {
      console.error('Error purchasing gift:', error)
      throw error
    }
  }

  /**
   * Confirm a gift payment after Stripe processing
   */
  static async confirmGiftPayment(paymentIntentId: string, status: string): Promise<{
    success: boolean
    giftId: string
    message?: string
    error?: string
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('User must be authenticated')
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-gift-payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentIntentId, status }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to confirm gift payment')
      }

      return await response.json()
    } catch (error) {
      console.error('Error confirming gift payment:', error)
      throw error
    }
  }

  /**
   * Process Stripe payment for a gift
   */
  static async processStripePayment(
    clientSecret: string,
    paymentDetails: {
      email: string
      name: string
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const stripe = await stripePromise
      if (!stripe) {
        // If Stripe is not available, simulate success for demo purposes
        console.warn('Stripe not available - simulating successful payment for demo')
        await new Promise(resolve => setTimeout(resolve, 2000))
        return { success: true }
      }

      // For demo purposes, we'll simulate a successful payment
      // In a real implementation, you would collect payment method details
      // and confirm the payment intent with Stripe Elements
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // In a real implementation, you would do:
      // const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      //   payment_method: {
      //     card: cardElement,
      //     billing_details: {
      //       name: paymentDetails.name,
      //       email: paymentDetails.email,
      //     },
      //   }
      // })

      // For demo, we'll return success
      return { success: true }
    } catch (error) {
      console.error('Error processing Stripe payment:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Payment processing failed' 
      }
    }
  }

  /**
   * Redeem a gift using a gift code
   */
  static async redeemGift(giftCode: string): Promise<{
    success: boolean
    giftType?: 'subscription' | 'credits'
    valueAmount?: number
    error?: string
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User must be authenticated to redeem gifts')
      }

      // Call the database function to redeem the gift
      const { data, error } = await supabase.rpc('redeem_gift', {
        gift_code_param: giftCode,
        user_id_param: user.id
      })

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      console.error('Error redeeming gift:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to redeem gift'
      }
    }
  }

  /**
   * Get gifts purchased by the current user
   */
  static async getPurchasedGifts(): Promise<GiftRecord[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User must be authenticated')
      }

      const { data, error } = await supabase
        .from('gifts')
        .select('*')
        .eq('purchaser_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Error fetching purchased gifts:', error)
      return []
    }
  }

  /**
   * Get gifts received by the current user
   */
  static async getReceivedGifts(): Promise<GiftRecord[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User must be authenticated')
      }

      const { data, error } = await supabase
        .from('gifts')
        .select('*')
        .or(`recipient_id.eq.${user.id},recipient_email.eq.${user.email}`)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Error fetching received gifts:', error)
      return []
    }
  }

  /**
   * Check if a gift code is valid
   */
  static async validateGiftCode(giftCode: string): Promise<{
    valid: boolean
    gift?: {
      title: string
      description: string
      type: 'subscription' | 'credits'
      valueAmount: number
      purchaserName: string
      expiresAt: string
    }
    error?: string
  }> {
    try {
      const { data, error } = await supabase
        .from('gifts')
        .select('title, description, gift_type, value_amount, purchaser_name, expires_at, status, payment_status')
        .eq('gift_code', giftCode)
        .single()

      if (error || !data) {
        return {
          valid: false,
          error: 'Invalid gift code'
        }
      }

      if (data.status === 'redeemed') {
        return {
          valid: false,
          error: 'This gift has already been redeemed'
        }
      }

      if (new Date(data.expires_at) < new Date()) {
        return {
          valid: false,
          error: 'This gift has expired'
        }
      }

      if (data.payment_status !== 'succeeded') {
        return {
          valid: false,
          error: 'This gift payment was not successful'
        }
      }

      return {
        valid: true,
        gift: {
          title: data.title,
          description: data.description,
          type: data.gift_type,
          valueAmount: data.value_amount,
          purchaserName: data.purchaser_name,
          expiresAt: data.expires_at
        }
      }
    } catch (error) {
      console.error('Error validating gift code:', error)
      return {
        valid: false,
        error: 'Failed to validate gift code'
      }
    }
  }

  /**
   * Send a gift email (this would typically be handled by the backend)
   */
  static async sendGiftEmail(giftId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('User must be authenticated')
      }

      // Get gift details first
      const { data: gift, error: giftError } = await supabase
        .from('gifts')
        .select('*')
        .eq('id', giftId)
        .single()

      if (giftError || !gift) {
        throw new Error('Gift not found')
      }

      // Send email through edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-gift-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          giftId: gift.id,
          recipientEmail: gift.recipient_email,
          giftCode: gift.gift_code,
          giftTitle: gift.title,
          giftDescription: gift.description,
          senderName: gift.purchaser_name,
          giftMessage: gift.gift_message
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to send gift email')
      }

      return { success: true }
    } catch (error) {
      console.error('Error sending gift email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send gift email'
      }
    }
  }
}

export default GiftService 